import { z } from "zod";
import { DocumentSnapshot, Timestamp } from "firebase/firestore";
import { NotFoundError } from "../error-classes";
import { dropUndefined, guardIsPlainObject } from "../utils";

// Email campaign status enum
export enum EmailCampaignStatus {
  PENDING = "pending",
  SENDING = "sending",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

// Individual email delivery status
export enum EmailDeliveryStatus {
  PENDING = "pending",
  SENT = "sent",
  DELIVERED = "delivered",
  BOUNCED = "bounced",
  SPAM_COMPLAINT = "spam_complaint",
  FAILED = "failed",
}

// Email recipient interface
export interface EmailRecipient {
  customerID: string;
  customerName: string;
  emailAddress: string;
  status: EmailDeliveryStatus;
  postmarkMessageID?: string; // Postmark message ID for tracking
  sentAt?: Timestamp;
  deliveredAt?: Timestamp;
  openedAt?: Timestamp;
  clickedAt?: Timestamp;
  bouncedAt?: Timestamp;
  spamComplaintAt?: Timestamp;
  errorMessage?: string;
}

export interface EmailCampaign {
  templateID: string;
  templateName: string; // Snapshot of template name for historical reference
  campaignName: string;
  description?: string;
  status: EmailCampaignStatus;
  recipients?: EmailRecipient[];
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  openedCount: number;
  clickedCount: number;
  bouncedCount: number;
  spamComplaintCount: number;
  failedCount: number;
  createdBy: string; // User UID
  timestampCreated: Timestamp;
  timestampStarted?: Timestamp;
  timestampCompleted?: Timestamp;
  timestampLastModified: Timestamp;
  deleted: boolean;
  // Rendered content snapshots for historical tracking
  renderedSubject: string;
  renderedHtmlContent: string;
}

export interface ExistingEmailCampaign extends EmailCampaign {
  id: string;
  refPath: string;
}

export interface NewEmailCampaign {
  templateID: string;
  templateName: string;
  campaignName: string;
  description?: string;
  recipients: EmailRecipient[];
  createdBy: string;
  renderedSubject: string;
  renderedHtmlContent: string;
}

const EmailRecipientSchema = z.object({
  customerID: z.string().min(1).max(200),
  customerName: z.string().min(1).max(200),
  emailAddress: z.string().email(),
  status: z
    .nativeEnum(EmailDeliveryStatus)
    .default(EmailDeliveryStatus.PENDING),
  postmarkMessageID: z.string().optional(), // Postmark message ID for tracking
  sentAt: z.instanceof(Timestamp).optional(),
  deliveredAt: z.instanceof(Timestamp).optional(),
  openedAt: z.instanceof(Timestamp).optional(),
  clickedAt: z.instanceof(Timestamp).optional(),
  bouncedAt: z.instanceof(Timestamp).optional(),
  spamComplaintAt: z.instanceof(Timestamp).optional(),
  errorMessage: z.string().optional(),
});

const EmailCampaignSchema = z.object({
  templateID: z.string().min(1).max(200),
  templateName: z.string().min(1).max(200), // Snapshot of template name for historical reference
  campaignName: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  status: z
    .nativeEnum(EmailCampaignStatus)
    .default(EmailCampaignStatus.PENDING),
  recipients: z.array(EmailRecipientSchema).optional(),
  totalRecipients: z.number().int().min(0),
  sentCount: z.number().int().min(0).default(0),
  deliveredCount: z.number().int().min(0).default(0),
  openedCount: z.number().int().min(0).default(0),
  clickedCount: z.number().int().min(0).default(0),
  bouncedCount: z.number().int().min(0).default(0),
  spamComplaintCount: z.number().int().min(0).default(0),
  failedCount: z.number().int().min(0).default(0),
  createdBy: z.string().min(1).max(200), // User UID
  timestampCreated: z.instanceof(Timestamp),
  timestampStarted: z.instanceof(Timestamp).optional(),
  timestampCompleted: z.instanceof(Timestamp).optional(),
  timestampLastModified: z.instanceof(Timestamp),
  deleted: z.boolean().default(false),
  // Rendered content snapshots for historical tracking
  renderedSubject: z.string().min(1).max(200),
  renderedHtmlContent: z.string().min(1).max(500000),
});

export const EmailCampaignManager = {
  /**
   * Convert the Document Snapshot into a validated ExistingEmailCampaign object.
   */
  createFromSnapshot: createEmailCampaignFromSnapshot,
  /** Drop `id` and `refPath` before saving to the database */
  convertForFirestore: convertEmailCampaignForFirestore,
  /** Use when validating something outgoing - writing to the DB or reading from the user */
  parse: validateEmailCampaign,
  /** Create a new email campaign from a NewEmailCampaign request */
  createFromNewCampaign: createFromNewCampaign,
  /** Calculate engagement metrics */
  getEngagementMetrics: getEngagementMetrics,
  /** Check if campaign is in progress */
  isInProgress: isInProgress,
  /** Check if campaign is completed */
  isCompleted: isCompleted,
};

/**
 * Create a new email campaign from a NewEmailCampaign request
 */
function createFromNewCampaign(newCampaign: NewEmailCampaign): EmailCampaign {
  const now = Timestamp.now();

  return {
    templateID: newCampaign.templateID,
    templateName: newCampaign.templateName,
    campaignName: newCampaign.campaignName,
    description: newCampaign.description,
    status: EmailCampaignStatus.PENDING,
    recipients: newCampaign.recipients,
    totalRecipients: newCampaign.recipients.length,
    sentCount: 0,
    deliveredCount: 0,
    openedCount: 0,
    clickedCount: 0,
    bouncedCount: 0,
    spamComplaintCount: 0,
    failedCount: 0,
    createdBy: newCampaign.createdBy,
    timestampCreated: now,
    timestampLastModified: now,
    deleted: false,
    renderedSubject: newCampaign.renderedSubject,
    renderedHtmlContent: newCampaign.renderedHtmlContent,
  };
}

/**
 * Convert the Document Snapshot into a validated ExistingEmailCampaign object.
 */
function createEmailCampaignFromSnapshot(
  snapshot: DocumentSnapshot,
): ExistingEmailCampaign {
  if (!snapshot.exists()) {
    throw new NotFoundError(
      `EmailCampaign document does not exist: ${snapshot.ref.path}`,
    );
  }

  const data = snapshot.data();
  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    ...validateEmailCampaign(data),
  };
}

function convertEmailCampaignForFirestore(emailCampaign: EmailCampaign): any {
  const local = Object.assign({}, emailCampaign);
  return dropUndefined(local);
}

function validateEmailCampaign(value: unknown): EmailCampaign {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  return EmailCampaignSchema.parse(value);
}

// Calculate engagement metrics
function getEngagementMetrics(emailCampaign: EmailCampaign) {
  const openRate =
    emailCampaign.deliveredCount > 0
      ? (emailCampaign.openedCount / emailCampaign.deliveredCount) * 100
      : 0;
  const clickRate =
    emailCampaign.deliveredCount > 0
      ? (emailCampaign.clickedCount / emailCampaign.deliveredCount) * 100
      : 0;
  const bounceRate =
    emailCampaign.totalRecipients > 0
      ? (emailCampaign.bouncedCount / emailCampaign.totalRecipients) * 100
      : 0;
  const deliveryRate =
    emailCampaign.totalRecipients > 0
      ? (emailCampaign.deliveredCount / emailCampaign.totalRecipients) * 100
      : 0;

  return {
    openRate: Math.round(openRate * 100) / 100,
    clickRate: Math.round(clickRate * 100) / 100,
    bounceRate: Math.round(bounceRate * 100) / 100,
    deliveryRate: Math.round(deliveryRate * 100) / 100,
  };
}

// Check if campaign is in progress
function isInProgress(emailCampaign: EmailCampaign): boolean {
  return (
    emailCampaign.status === EmailCampaignStatus.PENDING ||
    emailCampaign.status === EmailCampaignStatus.SENDING
  );
}

// Check if campaign is completed
function isCompleted(emailCampaign: EmailCampaign): boolean {
  return (
    emailCampaign.status === EmailCampaignStatus.COMPLETED ||
    emailCampaign.status === EmailCampaignStatus.FAILED ||
    emailCampaign.status === EmailCampaignStatus.CANCELLED
  );
}
